import React, { useMemo } from 'react';
import { LeadRecord, OpportunityRecord, LeadSource } from '../../types/throughput';
import { Card } from '../ui/Card';
import { clsx } from 'clsx';
interface LeadSourceLeaderboardProps {
  leads: LeadRecord[];
  opportunities: OpportunityRecord[];
  activeSource?: LeadSource | null;
  onSourceClick?: (source: LeadSource) => void;
}
export function LeadSourceLeaderboard({
  leads,
  opportunities,
  activeSource,
  onSourceClick
}: LeadSourceLeaderboardProps) {
  const rows = useMemo(() => {
    const sources: LeadSource[] = ['Web', 'Referral', 'Partner', 'Phone', 'Email', 'Other'];
    return sources.map(source => {
      const sourceLeads = leads.filter(l => l.source === source);
      const sourceLeadIds = new Set(sourceLeads.map(l => l.id));
      const sourceOpps = opportunities.filter(o => sourceLeadIds.has(o.leadId));
      const funded = sourceOpps.filter(o => o.fundedDate).length;
      const claimed = sourceLeads.length;
      const totalCost = sourceLeads.reduce((sum, l) => sum + l.costPerLead, 0);
      const totalDials = sourceLeads.reduce((sum, l) => sum + l.dialsBeforeClaim, 0);
      return {
        source,
        claimed,
        funded,
        pullThrough: claimed > 0 ? funded / claimed * 100 : 0,
        costPerFunding: funded > 0 ? totalCost / funded : 0,
        avgDials: claimed > 0 ? totalDials / claimed : 0
      };
    }).filter(r => r.claimed > 0).sort((a, b) => {
      if (a.costPerFunding === 0) return 1;
      if (b.costPerFunding === 0) return -1;
      return a.costPerFunding - b.costPerFunding;
    });
  }, [leads, opportunities]);
  if (rows.length === 0) return null;
  return <div className="flex flex-col gap-3 mb-8" data-id="element-73">
      <h2 className="text-xl font-semibold text-[#181818] mb-2" data-id="element-74">
        Lead Source Leaderboard
      </h2>
      {rows.map((row, index) => {
      const isActive = activeSource === row.source;
      const rank = index + 1;
      let recommendation = {
        text: 'Maintain',
        color: 'text-[#706E6B]',
        bg: 'bg-[#F5F5F5]'
      };
      if (rank === 1 || rank === 2) recommendation = {
        text: 'Invest More',
        color: 'text-[#04844B]',
        bg: 'bg-[#04844B]/10'
      };
      if (rank >= rows.length - 1 && rows.length > 3) recommendation = {
        text: 'Reduce',
        color: 'text-[#EA001E]',
        bg: 'bg-[#EA001E]/10'
      };
      return <Card key={row.source} className={clsx('p-4 flex flex-col sm:flex-row sm:items-center transition-all duration-200 cursor-pointer gap-4 sm:gap-0', isActive ? 'ring-2 ring-[#B8960C] bg-[#FBF7E8] shadow-md' : 'hover:border-[#B8960C]/50 bg-white')} onClick={() => onSourceClick?.(row.source)} noPadding data-id="element-75">
            <div className="flex items-center" data-id="element-76">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#F5F5F5] mr-4 font-bold text-[#706E6B] shrink-0" data-id="element-77">
                #{rank}
              </div>

              <div className="w-32 font-semibold text-[#181818]" data-id="element-78">
                {row.source}
              </div>
            </div>

            <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-79">
              <div className="flex flex-col" data-id="element-80">
                <span className="text-[10px] text-[#706E6B] uppercase tracking-wider" data-id="element-81">
                  Cost / Funding
                </span>
                <span className="text-lg font-semibold text-[#181818]" data-id="element-82">
                  {row.costPerFunding > 0 ? `$${row.costPerFunding.toFixed(0)}` : 'N/A'}
                </span>
              </div>
              <div className="flex flex-col" data-id="element-83">
                <span className="text-[10px] text-[#706E6B] uppercase tracking-wider" data-id="element-84">
                  Pull-through
                </span>
                <span className="text-sm font-medium text-[#181818] mt-1" data-id="element-85">
                  {row.pullThrough.toFixed(1)}%
                </span>
              </div>
              <div className="flex flex-col" data-id="element-86">
                <span className="text-[10px] text-[#706E6B] uppercase tracking-wider" data-id="element-87">
                  Avg Dials
                </span>
                <span className="text-sm font-medium text-[#181818] mt-1" data-id="element-88">
                  {row.avgDials.toFixed(1)}
                </span>
              </div>
              <div className="flex flex-col" data-id="element-89">
                <span className="text-[10px] text-[#706E6B] uppercase tracking-wider" data-id="element-90">
                  Funded
                </span>
                <span className="text-sm font-medium text-[#181818] mt-1" data-id="element-91">
                  {row.funded}
                </span>
              </div>
            </div>

            <div className="sm:ml-4 flex justify-end" data-id="element-92">
              <span className={clsx('px-3 py-1 rounded-full text-xs font-semibold', recommendation.bg, recommendation.color)} data-id="element-93">
                {recommendation.text}
              </span>
            </div>
          </Card>;
    })}
    </div>;
}